import * as React from 'react';
import styled from 'styled-components';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import slugify from 'slugify';
import { GradientHeading, Text } from '@components/Typography';
import TwitterIcon from '../images/twitter.svg';
import LinkedinIcon from '../images/linkedin.svg';
import WebsiteIcon from '../images/website.svg';
import RegisterBtn from '@components/RegisterBtn';

type Props = GatsbyTypes.PersonFragment & {
  mode: string;
  copy: GatsbyTypes.MicrocopyFragment['registration'];
};

export const StyledContent = styled.article`
  height: calc(100% - 84px);
  align-self: center;
  display: flex;
  flex-direction: column;
  text-align: center;
  justify-content: space-between;

  p {
    margin: 1rem auto 0;
    max-width: 80ch;
  }

  h2 {
    max-width: 18ch;
    margin: 0 auto;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    height: calc(100% - 76px);
  }
`;

const StyledAvatar = styled.div`
  height: fit-content;
  width: fit-content;
  background-color: var(--color-white);
  border-radius: 50%;
  margin: 0 auto;
  overflow: hidden;
`;

export const StyledImage = styled(GatsbyImage)`
  height: 95px;
  width: 95px;

  @media (min-height: 800px) {
    height: 172px;
    width: 172px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 95px;
    width: 95px;
  }
`;

const StyledSocialBar = styled.div`
  max-width: 626px;
  min-width: 200px;
  margin: 1rem auto 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: center;
  justify-content: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: row;
    gap: 1.5rem;
  }
`;

const StyledLink = styled.a`
  display: flex;
  align-content: center;
  height: 13px;

  svg {
    display: inline-block;
    margin-right: 0.4rem;
    cursor: pointer;
    height: 100%;
  }

  span {
    display: inline-block;
    cursor: pointer;
    font-size: 10px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 22px;

    span {
      font-size: 1.125rem;
    }
  }
`;

const Bio = styled(Text).attrs({ mode: 'dark', as: 'p', size: 'xs' })`
  a {
    text-decoration: underline;
  }
`;

const PersonModal = ({ company, name, title, bio, social, image, session, mode, copy }: Props): JSX.Element => {
  const displayImage = getImage(image as any);

  const displayWebsite = (url: string): string | undefined => {
    return url.split('//').pop();
  };

  return (
    <>
      <StyledContent data-link-id={slugify(name.toLowerCase())}>
        <StyledAvatar>
          <StyledImage image={displayImage} alt={name ?? ''} />
        </StyledAvatar>
        <GradientHeading as="h2">{name}</GradientHeading>
        <Text mode="dark" size="xl" as="p" weight="bold">
          {`${title}, ${company}`}
        </Text>
        <StyledSocialBar>
          {social?.twitter && (
            <StyledLink href={`https://twitter.com/${social.twitter}`} target="_blank">
              <TwitterIcon />
              <Text mode="dark">@{social.twitter}</Text>
            </StyledLink>
          )}
          {social?.linkedin && (
            <StyledLink href={`https://linkedin.com/in/${social.linkedin}`} target="_blank">
              <LinkedinIcon />
              <Text mode="dark">{social.linkedin}</Text>
            </StyledLink>
          )}
          {social?.website && (
            <StyledLink href={social.website} target="_blank">
              <WebsiteIcon />
              <Text mode="dark">{displayWebsite(social.website)}</Text>
            </StyledLink>
          )}
        </StyledSocialBar>
        {bio && <Bio dangerouslySetInnerHTML={{ __html: bio }} />}
      </StyledContent>
      <RegisterBtn sessionId={session ? session.brandLiveUrl : name?.toLowerCase()} type="speaker">
        {copy[mode]}
      </RegisterBtn>
    </>
  );
};

export default PersonModal;
